import {
  filterEnzymes,
  filterFeatures,
  filterPrimers,
} from "@components/search/useSearchResults";
import { useSequenceCreateStore } from "@components/sequence-create/sequenceCreateStore";
import {
  AlignmentIcon,
  PhyloTreeIcon,
  SequenceCollectionIcon,
  SequenceIcon,
} from "@components/tree/icons";
import { db } from "@db";
import type {
  GnomuxDirectory,
  GnomuxFile,
  GnomuxObjectType,
} from "@db/objects";
import { getObjectPath } from "@db/path";
import { useAllPrimers } from "@db/primers";
import { getAllObjects, getFileByContent, getObjects } from "@db/tree";
import type { CommandAction } from "@lib/commandRegistry";
import { commands, groupOrder } from "@lib/commandRegistry";
import { useFeatureBadgeStore } from "@lib/featureBadgeStore";
import { naturalCompare } from "@lib/sort";
import { cn } from "@lib/styling";
import { useIsFeatureNew } from "@lib/useIsFeatureNew";
import { useNavigate } from "@tanstack/react-router";
import { Badge } from "@ui/badge";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandItemSubtext,
  CommandList,
  CommandShortcut,
} from "@ui/command";
import { useLiveQuery } from "dexie-react-hooks";
import {
  CircleHelpIcon,
  FlaskConicalIcon,
  FolderIcon,
  ScissorsIcon,
  TagIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { enzymes } from "restriction-enzymes";
import { type TypeFilter, useCommandPaletteStore } from "./commandPaletteStore";

/**
 * The maximum number of items to display in each command palette group.
 */
const MAX_GROUP_ITEMS = 7;

const typeFilterOptions: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "recents", label: "Recents" },
  { value: "files", label: "Files" },
  { value: "commands", label: "Commands" },
  { value: "references", label: "References" },
  { value: "settings", label: "Settings" },
];

function getFileTypeKeyword(type: GnomuxObjectType): string {
  switch (type) {
    case "GnomuxAlignment":
      return "alignment";
    case "GnomuxSequence":
      return "sequence";
    case "GnomuxSequenceCollection":
      return "sequence collection";
    case "GnomuxTree":
      return "tree";
  }
}

function FileTypeIcon({ type }: { type: GnomuxObjectType }) {
  switch (type) {
    case "GnomuxAlignment":
      return <AlignmentIcon />;
    case "GnomuxSequence":
      return <SequenceIcon />;
    case "GnomuxSequenceCollection":
      return <SequenceCollectionIcon />;
    case "GnomuxTree":
      return <PhyloTreeIcon />;
  }
}

const commandsByGroup = groupOrder.map((group) => ({
  group,
  entries: commands.filter((cmd) => cmd.group === group),
}));

function NewBadge({ featureId }: { featureId: string }) {
  const isNew = useIsFeatureNew(featureId);
  if (!isNew) return null;
  return (
    <Badge variant="secondary" className="ml-auto px-1.5 py-0 text-[10px]">
      New
    </Badge>
  );
}

function TypeFilterChips() {
  const typeFilter = useCommandPaletteStore((s) => s.typeFilter);
  const setTypeFilter = useCommandPaletteStore((s) => s.setTypeFilter);

  return (
    <div className="flex gap-1 border-b px-3 py-2">
      {typeFilterOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs transition-colors",
            typeFilter === option.value
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/80",
          )}
          onClick={() => setTypeFilter(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function buildMaps(objects: (GnomuxFile | GnomuxDirectory)[]) {
  const parentById = new Map<string, string>();
  const namesById = new Map<string, string>();

  for (const obj of objects) {
    namesById.set(obj.id, obj.name);
    const file = obj as GnomuxFile;
    if (file.parent) {
      parentById.set(obj.id, file.parent);
    }
  }

  return { parentById, namesById };
}

export function CommandPalette() {
  const { open, setOpen, toggle, typeFilter } = useCommandPaletteStore();
  const navigate = useNavigate();
  const dismissFeature = useFeatureBadgeStore((s) => s.dismissFeature);
  const openSequenceCreateDialog = useSequenceCreateStore(
    (state) => state.openDialog,
  );

  const [search, setSearch] = useState("");

  const objects = useLiveQuery(async () => {
    if (!open) return undefined;
    const objects = await getAllObjects();
    return objects.sort((a, b) => naturalCompare(a.name, b.name));
  }, [open]);

  const recentObjects = useLiveQuery(async () => {
    if (!open) return undefined;
    const recents = await db.recents
      .orderBy("viewedAt")
      .reverse()
      .limit(MAX_GROUP_ITEMS)
      .toArray();
    const objectIds = recents.map((r) => r.objectId);
    const objects = await getObjects(objectIds);
    return objects.filter((o): o is GnomuxFile => o != null && "type" in o);
  }, [open]);

  const { parentById, namesById } = useMemo(
    () =>
      objects
        ? buildMaps(objects)
        : { parentById: new Map(), namesById: new Map() },
    [objects],
  );

  const primers = useAllPrimers();
  const allFeatures = useLiveQuery(async () => {
    if (!open) return undefined;
    return db.features.toArray();
  }, [open]);
  const sequenceNamesById = useLiveQuery(async () => {
    if (!allFeatures || allFeatures.length === 0)
      return new Map<string, string>();
    const seqIds = [...new Set(allFeatures.map((f) => f.sequenceId))];
    const sequences = await db.sequences.bulkGet(seqIds);
    const map = new Map<string, string>();
    for (const seq of sequences) {
      if (seq) map.set(seq.id, seq.name);
    }
    return map;
  }, [allFeatures]);

  const trimmedSearch = search.trim().toLowerCase();
  const hasSearch = trimmedSearch.length > 0;

  const filteredPrimers = useMemo(
    () => filterPrimers(trimmedSearch, primers),
    [trimmedSearch, primers],
  );
  const filteredFeatures = useMemo(
    () => filterFeatures(trimmedSearch, allFeatures),
    [trimmedSearch, allFeatures],
  );
  const filteredEnzymes = useMemo(
    () => filterEnzymes(trimmedSearch, enzymes),
    [trimmedSearch],
  );

  useHotkeys(
    "mod+k",
    (e) => {
      e.preventDefault();
      toggle();
    },
    { scopes: ["global"], enableOnFormTags: true },
  );

  function handleSelect(path: string, featureId?: string) {
    setOpen(false);
    if (featureId) dismissFeature(featureId);
    navigate({ to: path });
  }

  function handleCommandSelect(action: CommandAction, featureId?: string) {
    setOpen(false);
    if (featureId) dismissFeature(featureId);
    if (action.type === "navigate") {
      navigate({ to: action.to });
    } else {
      openSequenceCreateDialog(action.mode);
    }
  }

  async function handleFeatureSelect(sequenceId: string) {
    const file = await getFileByContent(sequenceId);
    if (file) {
      handleSelect(`/view/${file.id}`);
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) setSearch("");
  }

  const showRecents =
    typeFilter === "all" || typeFilter === "recents" || typeFilter === "files";
  const showFiles = typeFilter === "all" || typeFilter === "files";
  const showCommands = typeFilter === "all" || typeFilter === "commands";
  const showReferences = typeFilter === "all" || typeFilter === "references";
  const showSettings = typeFilter === "all" || typeFilter === "settings";

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      showCloseButton={false}
    >
      <CommandInput
        placeholder="Search..."
        value={search}
        onValueChange={setSearch}
      />
      <TypeFilterChips />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {showRecents && recentObjects && recentObjects.length > 0 && (
          <CommandGroup heading="Recent">
            {recentObjects.map((obj) => {
              const path = getObjectPath(obj.id, parentById, namesById);
              return (
                <CommandItem
                  key={`recent-${obj.id}`}
                  keywords={[getFileTypeKeyword(obj.type)]}
                  onSelect={() => handleSelect(`/view/${obj.id}`)}
                >
                  <FileTypeIcon type={obj.type} />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate">{`Open ${obj.name}`}</span>
                    <CommandItemSubtext>{path}</CommandItemSubtext>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {showFiles && (
          <CommandGroup heading="Data">
            {objects?.slice(0, MAX_GROUP_ITEMS).map((obj) => {
              const path = getObjectPath(obj.id, parentById, namesById);

              if ("type" in obj) {
                return (
                  <CommandItem
                    key={obj.id}
                    keywords={[getFileTypeKeyword(obj.type)]}
                    onSelect={() => handleSelect(`/view/${obj.id}`)}
                  >
                    <FileTypeIcon type={obj.type} />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{`Open ${obj.name}`}</span>
                      <CommandItemSubtext>{path}</CommandItemSubtext>
                    </div>
                  </CommandItem>
                );
              }

              return (
                <CommandItem
                  key={obj.id}
                  keywords={["folder", "directory"]}
                  onSelect={() => setOpen(false)}
                >
                  <FolderIcon />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate">{`Open ${obj.name}`}</span>
                    <CommandItemSubtext>{path}</CommandItemSubtext>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {showCommands &&
          commandsByGroup
            .filter(({ group }) => group !== "Settings")
            .map(({ group, entries }) => (
              <CommandGroup key={group} heading={group}>
                {entries.map((cmd) => (
                  <CommandItem
                    key={cmd.id}
                    keywords={cmd.keywords}
                    onSelect={() => handleCommandSelect(cmd.action, cmd.id)}
                  >
                    <cmd.icon />
                    {cmd.label}
                    <NewBadge featureId={cmd.id} />
                    {cmd.shortcut && (
                      <CommandShortcut>{cmd.shortcut}</CommandShortcut>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}

        {showReferences && hasSearch && filteredPrimers.length > 0 && (
          <CommandGroup heading={`Primers (${filteredPrimers.length})`}>
            {filteredPrimers.slice(0, MAX_GROUP_ITEMS).map((primer) => (
              <CommandItem
                key={primer.id}
                keywords={[primer.name, "primer"]}
                onSelect={() => handleSelect("/library/primers")}
              >
                <FlaskConicalIcon />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate">{primer.name}</span>
                  <CommandItemSubtext>
                    {primer.direction === "forward" ? "Forward" : "Reverse"} ·{" "}
                    {primer.tm.toFixed(1)}°C
                  </CommandItemSubtext>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {showReferences && hasSearch && filteredEnzymes.length > 0 && (
          <CommandGroup
            heading={`Restriction Enzymes (${filteredEnzymes.length})`}
          >
            {filteredEnzymes.slice(0, MAX_GROUP_ITEMS).map((enzyme) => (
              <CommandItem
                key={enzyme.name}
                keywords={[enzyme.name, enzyme.site, "enzyme", "restriction"]}
                onSelect={() => handleSelect("/reference/enzymes")}
              >
                <ScissorsIcon />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate">{enzyme.name}</span>
                  <CommandItemSubtext>{enzyme.site}</CommandItemSubtext>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {showReferences && hasSearch && filteredFeatures.length > 0 && (
          <CommandGroup heading={`Features (${filteredFeatures.length})`}>
            {filteredFeatures.slice(0, MAX_GROUP_ITEMS).map((feature) => (
              <CommandItem
                key={feature.id}
                keywords={[feature.name, feature.type, "feature"]}
                onSelect={() => handleFeatureSelect(feature.sequenceId)}
              >
                <TagIcon />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate">{feature.name}</span>
                  <CommandItemSubtext>
                    {feature.type} ·{" "}
                    {sequenceNamesById?.get(feature.sequenceId) ?? ""}
                  </CommandItemSubtext>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {showSettings &&
          commandsByGroup
            .filter(({ group }) => group === "Settings")
            .map(({ group, entries }) => (
              <CommandGroup key={group} heading={group}>
                {entries.map((cmd) => (
                  <CommandItem
                    key={cmd.id}
                    keywords={cmd.keywords}
                    onSelect={() => handleCommandSelect(cmd.action, cmd.id)}
                  >
                    <cmd.icon />
                    {cmd.label}
                    <NewBadge featureId={cmd.id} />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}

        <CommandGroup heading="Help">
          <CommandItem
            keywords={["help", "guide", "getting started", "learn"]}
            onSelect={() => handleSelect("/help")}
          >
            <CircleHelpIcon />
            Open Help
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
